import React, { useEffect, useMemo, memo, useState, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import B2BLayout from '../../../layouts/b2b'
import {
  createSession,
  getAppealId,
  getAppealsFormError,
  getAppealsFormData,
  getVisibleFormData,
} from './selectors'
import { DefaultJsonFormContextProps } from '../../../components/v2/default-json-form/context/default-json-form-context-props'
import { getAppealsEnums, getAppealsForm } from '../../../store/appeal/action-creators'
import DefaultJsonFormContext from '../../../components/v2/default-json-form/context/default-json-form-context'
import { AppealsTabs } from './ui/tabs/appeals-tabs'
import { useAppDispatch } from '../../../hooks/useAppDispatch/useAppDispatch'
import AppealsFormsHeader from './ui/appeals-forms/appeals-form-header/appeals-form-header'
import { useSaveResult } from './hooks/useSaveResult'
import { Modal } from 'antd'
import { clearAppealsFormFailed } from '../../../store/appeal/actions'

const AppealCardPage = memo(() => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const appealId = useSelector(getAppealId)
  const entityType = 'appeal'
  const [modal, contextHolder] = Modal.useModal()
  const appealsFormError = useSelector(getAppealsFormError)
  const { sessionData } = useSelector(createSession)
  const isShowByAccess = sessionData.roles.includes(19)
  const appealsFormData = useSelector(getAppealsFormData)
  const visibleFormData = useSelector(getVisibleFormData)
  const { saveResult } = useSaveResult()
  const [isFormProcessing, setIsFormProcessing] = useState(false)

  const pageTitle = useMemo(() => (id === 'new' ? 'Новое обращение' : `Обращение №${id}`), [id])

  const breadcrumbs = useMemo(
    () => [
      {
        href: '/appeals',
        title: 'Обращения',
      },
      {
        title: pageTitle,
      },
    ],
    [pageTitle],
  )

  const defaultProps = useMemo(
    (): DefaultJsonFormContextProps => ({
      entityId: id,
      entityType: entityType,
    }),
    [id],
  )

  useEffect(() => {
    let mounted = true
    if (mounted) {
      const getInitPage = async id => {
        await dispatch(getAppealsEnums(id))
        await dispatch(getAppealsForm(id))
      }
      getInitPage(id).catch(console.error)
    }
    return () => {
      mounted = false
    }
  }, [id, isShowByAccess, dispatch])

  useEffect(() => {
    if (id === 'new' && appealId) {
      navigate({
        pathname: `/appeal/${appealId}`,
      })
    }
    return () => {}
  }, [id, appealId])

  const handleToOrder = useCallback(async () => {
    setIsFormProcessing(true)
    try {
      await saveResult(id, visibleFormData, appealsFormData?.stages, 'order')
    } finally {
      setIsFormProcessing(false)
    }
  }, [id, visibleFormData, appealsFormData, saveResult])

  useEffect(() => {
    if (!appealsFormError) return
    const content =
      typeof appealsFormError === 'object'
        ? appealsFormError.message || 'Неизвестная ошибка'
        : String(appealsFormError)
    modal.error({
      title: 'Ошибка',
      content,
      onOk: () => dispatch(clearAppealsFormFailed()),
    })
  }, [appealsFormError, dispatch, modal])

  if (!id) return null
  return (
    <B2BLayout breadcrumbs={breadcrumbs} pageTitle={pageTitle}>
      <DefaultJsonFormContext.Provider value={defaultProps}>
        <AppealsFormsHeader
          id={id}
          isFinished={
            appealsFormData?.stages &&
            appealsFormData?.stages?.current ===
              Object.keys(appealsFormData?.stages?.items).length - 1
          }
          stage={appealsFormData?.stages?.current}
          toOrder={handleToOrder}
          isFormProcessing={isFormProcessing}
        />
        <AppealsTabs id={id} isFormProcessing={isFormProcessing} />
      </DefaultJsonFormContext.Provider>
      {contextHolder}
    </B2BLayout>
  )
})

AppealCardPage.displayName = 'AppealCardPage'
export default memo(AppealCardPage)
